import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  TocItem,
  FaqContent as UIFoundationFaqContent
} from '@dcxwam/dcx-wam-ui-foundations'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import { FaqsArticleProps } from './types'
import type { Article } from '../../../../services/faqs/faqs-sdk'
import {
  executeLoadArticle,
  replaceFaqsQuery,
  buildArticleBreadcrumbItems,
  extractArticleCustomSeoAttributes
} from '../common/FaqsUtils'
import {
  extractTableOfContents,
  extractQuestionsFromHtml,
  generateFaqPageJsonLD,
  extractFirstParagraph
} from '../common/faq-schema.utils'
import FaqsArticleEditConfig from './FaqsArticleEditConfig'
import {
  setBreadcrumbTitle,
  BreadcrumbItem
} from '../../../../utils/breadcrumb-context'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

const FaqsArticle: FC<EditableComponentPropsType<FaqsArticleProps>> = (
  props
) => {
  const { model } = props
  const { articleSlug, faqConfig, initialArticle, initialTocItems, ...rest } =
    model || {}
  const isAuthor = AuthoringUtils.isInEditor()
  const editableProps = useExtractEditableProps(props, rest)

  const [article, setArticle] = useState<Article | null>(initialArticle ?? null)
  const [tocItems, setTocItems] = useState<TocItem[]>(initialTocItems ?? [])
  const [loading, setLoading] = useState(!initialArticle)

  const handleBackClick = useCallback(() => {
    window.history.back()
  }, [])

  const buildBreadcrumbItems = useCallback(
    (data: Article): BreadcrumbItem[] =>
      buildArticleBreadcrumbItems({
        data,
        categoryPagePath: faqConfig?.faqCategoryPagePath ?? '',
        origin: window?.location?.origin,
        categoryWithProduct: faqConfig?.categoryWithProduct
      }),
    [faqConfig?.faqCategoryPagePath, faqConfig?.categoryWithProduct]
  )

  useEffect(() => {
    if (!articleSlug || initialArticle) {
      if (initialArticle) {
        setBreadcrumbTitle(buildBreadcrumbItems(initialArticle))
      }
      setLoading(false)
      return
    }

    const loadArticle = async () => {
      setLoading(true)
      const data = await executeLoadArticle(articleSlug)
      if (data) {
        setArticle(data)
        setTocItems(extractTableOfContents(data?.articleContent ?? ''))
        setBreadcrumbTitle(buildBreadcrumbItems(data))
      }
      setLoading(false)
    }
    loadArticle()
  }, [articleSlug, initialArticle, buildBreadcrumbItems])

  const faqContentConfig = useMemo(
    () => ({
      title: article?.articleTitle ?? '',
      htmlContent: article?.articleContent ?? '',
      backLinkProps: {
        label: faqConfig?.backLinkLabel ?? '',
        onClick: handleBackClick
      },
      tocProps: {
        title: faqConfig?.tocSectionLabel ?? '',
        items: tocItems,
        activeItemIndex: 0
      }
    }),
    [article, tocItems, faqConfig, handleBackClick]
  )

  if (!isAuthor && (loading || !article)) {
    return null
  }
  return (
    <EditableComponent {...editableProps}>
      {FaqsArticleEditConfig.checkAuthor(isAuthor) ? (
        FaqsArticleEditConfig.configLabel
      ) : (
        <UIFoundationFaqContent {...faqContentConfig} />
      )}
    </EditableComponent>
  )
}

// @ts-expect-error Property 'getServerSideProps' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqsArticle.getServerSideProps = async (props: Record<string, any>) => {
  const { articleSlug, ...rest } = props

  if (!articleSlug) {
    return { ...rest, articleSlug }
  }

  const initialArticle = await executeLoadArticle(articleSlug)
  const initialTocItems = initialArticle
    ? extractTableOfContents(initialArticle.articleContent ?? '')
    : []
  const { customSeoTitle, customSeoDescription } =
    extractArticleCustomSeoAttributes(initialArticle)

  return {
    ...rest,
    articleSlug,
    initialArticle,
    initialTocItems,
    customSeoTitle,
    customSeoDescription
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqsArticle.getHeadData = async (props: Record<string, any>) => {
  const {
    seoTitle,
    customSeoTitle,
    customSeoDescription,
    articleSlug,
    initialArticle,
    faqConfig,
    pageUrl
  } = props ?? {}

  let pageSeoTitle = customSeoTitle || replaceFaqsQuery(seoTitle, null)

  if (!articleSlug) {
    return { status: 200, seoTitle: pageSeoTitle }
  }

  const data = initialArticle ?? (await executeLoadArticle(articleSlug))
  if (!data) {
    return { status: 200, seoTitle: pageSeoTitle }
  }

  pageSeoTitle = customSeoTitle || replaceFaqsQuery(seoTitle, data.articleTitle)
  const enableSchema = !!faqConfig?.enableFaqPageSchema
  let jsonLD = null

  if (enableSchema && data.articleContent) {
    const questions = extractQuestionsFromHtml(
      data.articleContent,
      data.articleTitle
    )
    jsonLD = generateFaqPageJsonLD(questions)
  }

  const categoryPagePath = faqConfig?.faqCategoryPagePath ?? ''
  let origin = ''
  if (pageUrl) {
    try {
      origin = new URL(pageUrl).origin
    } catch {
      // pageUrl is not a valid absolute URL, use relative paths
    }
  }
  const dynamicBreadcrumbItems = buildArticleBreadcrumbItems({
    data,
    categoryPagePath,
    origin: origin || undefined,
    categoryWithProduct: faqConfig?.categoryWithProduct,
    articlePageUrl: pageUrl
  })

  const pageSeoDescription =
    customSeoDescription || extractFirstParagraph(data.articleContent ?? '')

  return {
    status: 200,
    seoTitle: pageSeoTitle,
    seoDescription: pageSeoDescription,
    dynamicBreadcrumbItems,
    ...(jsonLD && { jsonLD })
  }
}

const FaqsArticleWithErrorBoundary = withErrorBoundary(FaqsArticle, {
  componentName: 'FaqsArticle',
  fallback: <ErrorFallback componentName="FaqsArticle" />
})

export default FaqsArticleWithErrorBoundary
